import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Tracks element width for libraries that need a numeric chart width (e.g. ApexCharts).
 * Uses a callback ref so layout runs when the node mounts (e.g. after loading finishes).
 * @returns {[React.RefCallback<HTMLElement|null>, number]}
 */
export default function useElementWidth() {
  const [element, setElement] = useState(null);
  const [width, setWidth] = useState(0);

  const ref = useCallback((node) => {
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!element) {
      setWidth(0);
      return undefined;
    }

    const update = () => {
      const w = element.getBoundingClientRect().width;
      setWidth(w > 0 ? Math.floor(w) : 0);
    };

    update();
    const rafId = requestAnimationFrame(update);

    const canUseResizeObserver = typeof ResizeObserver !== 'undefined';
    if (canUseResizeObserver) {
      const ro = new ResizeObserver(update);
      ro.observe(element);
      return () => {
        cancelAnimationFrame(rafId);
        ro.disconnect();
      };
    }

    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', update);
    };
  }, [element]);

  return [ref, width];
}
